import { useState, useEffect, useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Container, Col, Row, Button } from 'reactstrap'

import { activate, selectSessionStatus, clearErrorMessage } from 'slices/sessionSlice'

import { InputFormat, SinglePageCard } from 'components/common'
import * as Rules from 'components/common/FormFormat/ValidationRules'
import { COLUMN_SIZES } from 'components/common/constants'

import styles from './Activate.module.scss'

import type { MouseEvent } from 'react'

const Activate = () => {
  const [modalErrorMessage, setModalErrorMessage] = useState<string | undefined>(undefined)
  const [name, setName] = useState<string | undefined>(undefined)
  const [password, setPassword] = useState<string | undefined>(undefined)
  const [confirmation, setConfirmation] = useState<string | undefined>(undefined)
  const [nameValidity, setNameValidity] = useState(false)
  const [passwordValidity, setPasswordValidity] = useState(false)
  const [confirmationValidity, setConfirmationValidity] = useState(false)

  const dispatch = useDispatch()
  const { loggedIn, activated, errorMessage } = useSelector(selectSessionStatus, shallowEqual)
  const navigate = useNavigate()
  const onSubmit = (e: MouseEvent) => {
    e.preventDefault()
    if (name && password) {
      dispatch(activate({ name, password }))
    }
  }
  useEffect(() => {
    if (!loggedIn) {
      navigate('/signup')
      return
    }

    if (activated) {
      navigate('/login')
    }
  }, [navigate, loggedIn, activated])

  useEffect(() => {
    if (errorMessage !== '') {
      setModalErrorMessage('登録に失敗しました。')
    } else {
      setModalErrorMessage(undefined)
    }
  }, [errorMessage])

  const disabled = useMemo(
    () => !(name && nameValidity && password && passwordValidity && confirmation && confirmationValidity),
    [name, nameValidity, password, passwordValidity, confirmation, confirmationValidity]
  )

  return (
    <SinglePageCard
      title="アカウント情報"
      errorMessage={modalErrorMessage}
      onHideNotification={() => {
        setModalErrorMessage(undefined)
        dispatch(clearErrorMessage())
      }}
    >
      <Container>
        <InputFormat
          label="お名前"
          placeholder="お名前"
          value={name}
          size={COLUMN_SIZES.LARGE}
          maxLength={100}
          formText="アプリケーション上で表示されます。後で変更することも可能です。"
          autoComplete="name"
          onChange={value => setName(value)}
          validations={[Rules.Required]}
          onValidate={setNameValidity}
        />

        <InputFormat
          label="新しいパスワード"
          type="password"
          placeholder="新しいパスワード"
          value={password}
          size={COLUMN_SIZES.LARGE}
          maxLength={32}
          autoComplete="new-password"
          onChange={value => setPassword(value)}
          validations={[Rules.MinLength(8), Rules.Password]}
          onValidate={setPasswordValidity}
          className="mb-3"
          formText="新しいパスワード（半角英数字8文字以上・大文字・小文字・数字を含む）を入力してください。"
        />

        <InputFormat
          label="パスワード（確認）"
          type="password"
          placeholder="パスワード（確認）"
          value={confirmation}
          size={COLUMN_SIZES.LARGE}
          maxLength={32}
          autoComplete="new-password"
          onChange={value => setConfirmation(value)}
          validations={[Rules.MinLength(8), Rules.Password, Rules.PasswordConfirmation(password)]}
          onValidate={setConfirmationValidity}
        />

        <Row>
          <Col className="text-center mt-3">
            <Button color="primary" className={styles.button} disabled={disabled} onClick={onSubmit}>
              登録
            </Button>
          </Col>
        </Row>
      </Container>
    </SinglePageCard>
  )
}

export default Activate
